<template>
  <div class="ComposableTable d-flex" :id="id" :class="{noMinHeight, whiteBg}">
    <table>
      <Thead
        v-if="!removeTableHeader"
        :value="computedColumnSizes"
        @input="(sizes) => (columnSizes = sizes)"
        :columns="columns"
        :variables="variables"
        :tooltip="tooltip"
        :input-active="inputActive"
        :order="order"
        :sort="sort"
        :enableSortBySpecificColumn="enableSortBySpecificColumn"
        :enableColumnResize="enableColumnResize"
        :dragDisabledOnColumns="dragDisabledOnColumns"
        @set-order="(v) => $emit('set-order', v)"
        @set-sort="(v) => $emit('set-sort', v)"
        @update-column-order="(v) => $emit('update-column-order', v)"
        @set-filter="v => $emit('set-filter', v)"
      >
        <template #table-header="props">
          <slot
            name="table-header"
            :is-loading="!isReady"
            v-bind="props"
          ></slot>
        </template>
      </Thead>

      <tbody>
        <slot name="before-first-row"></slot>

        <template v-if="!isEmpty || !isReady">
          <tr
            v-for="(row, index) in dataOrPlaceholder"
            :key="index + 'th'"
            :id="`table-row-${row.id || row.uid || row.sku}`"
            :class="[
              { removeCursor, rowClickable: row._isReady && onRowClick },
              { 'table-danger': row.isDanger !== undefined ? row.isDanger : false },
              { 'table-success': row.isSuccess !== undefined ? row.isSuccess: false },
              { 'table-primary': row.isPrimary !== undefined ? row.isPrimary: false },
              { 'table-warning': row.isWarning !== undefined ? row.isWarning : false },
              'd-flex table-row']"
            @click="row._isReady && onRowClick && onRowClick(row)"
          >
            <slot
              name="column-select"
              :is-loading="!row._isReady"
              :selected="selected.includes(row && (row.uid || row.sku))"
              :item="row"
              :row-index="index"
            ></slot>
            <slot
              name="before-row"
              :is-loading="!row._isReady"
              :item="row"
              :row-index="index"
            ></slot>

            <td
              v-for="(field, i) in variables"
              :key="field"
              class="table-cell"
              :class="[field, {
                'first-child': i == 0,
                'last-table-cell': i === variables.length - 1,
              }]"
              :style="row && row[`_style_${field}`]"
            >
              <span
                v-if="
                  !row._isReady ||
                  row._loading_field === 'ALL' ||
                  (row._loading_field && row._loading_field.includes(field))
                "
                class="ske"
              >
                {{ index % 2 === 0 ? "ashtashta sasht asht " : "asht thsa" }}
              </span>

              <slot v-else
                :name="`cell-${field}`"
                :value="row[field]"
                :item="row"
              >
                <span v-if="field == 'store_type'" class="cell-content">
                  {{ store_type_field[`${row[field]}`] }}
                </span>

                <span v-else class="cell-content">
                  {{ row[field] }}
                </span>
              </slot>
            </td>

            <td class="row-action" @click.stop>
              <span style="position: relative">
                <slot
                  v-if="row._isReady"
                  name="row-action"
                  :item="row"
                  :row-index="index"
                ></slot>
              </span>
            </td>
          </tr>
        </template>

        <template v-else>
          <div
            class="
              d-flex
              justify-content-center
              align-items-center
              empty-state-wrapper
            "
          >
            <slot name="empty-state" :is-loading="!isReady"></slot>
          </div>
        </template>

        <slot name="after-last-row"></slot>
      </tbody>
    </table>
  </div>
</template>

<script>
import Thead from "./Thead.vue";

const SIZES_STORAGE_KEY = "sizes_storage_key:";

import { format } from "date-fns";

export default {
  components: {
    Thead,
  },
  props: {
    noMinHeight: Boolean,
    whiteBg: Boolean,
    data: Array,
    columns: Array,
    variables: Array,
    tooltip: {
      type: Object,
      required: false,
    },
    inputActive: {
      type: Object,
      required: false,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    ignoreDefaultWidth: Boolean,

    id: String,
    removeCursor: Boolean,
    onRowClick: Function,
    removeTableHeader: Boolean,
    disableIsLoadingTalueResult: Boolean,
    dateFormat: [Boolean, String],
    columnConfigurationId: String,
    enableSortBySpecificColumn: Boolean,
    enableColumnResize: Boolean,
    sort: String,
    order: String,
    dragDisabledOnColumns: Array,
    pageSize: Number,
  },
  data() {
    return {
      defaultCellWidth: null,
      columnSizes: null,
      store_type_field: {
        online_store: "Hoofdwebsite",
        wholesale : "Groothandel",
        physical_store: "Fysieke Winkel"
      }
    }
  },
  mounted() {
    this.calculateTheadCellWidths();
    this.getStoredSizes();
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    getStoredSizes() {
      let sizes = localStorage.getItem(this.sizesStorageKey);
      if (!sizes) return;

      try {
        this.columnSizes = JSON.parse(sizes);
      } catch (err) {}
    },
    calculateTheadCellWidths(onNextTick = true) {
      const calculate = () => {
        const firstHeaderElement = document.querySelector(
          `${this.id ? `#${this.id}` : ".ComposableTable"} th`
        );

        const width = firstHeaderElement
          ? firstHeaderElement.clientWidth
          : "100%";
        if (width && this.defaultCellWidth !== width)
          this.defaultCellWidth = width;
      };

      this.defaultCellWidth = null;
      onNextTick ? this.$nextTick(calculate) : calculate();
    },
    parseField(field) {
      if (field === undefined || field === null || field === "") return "---";

      if (
        typeof field === "number" &&
        ((field + "").includes(",") || (field + "").includes(".")) &&
        !isNaN(parseFloat(field))
      )
        return (parseFloat(field).toFixed(2) + "").replace(/\./, ",");

      if (typeof field !== "string" || !field[0]) return field;

      const possibleDate = new Date(field);
      if (
        possibleDate instanceof Date &&
        !isNaN(possibleDate.valueOf()) && // is date
        new Date(Date.parse(field)).toISOString() === field // is ISO date
      ) {
        return this.dateFormat
          ? format(possibleDate, this.dateFormat)
          : format(possibleDate, "dd-MM-yyyy HH:mm");
      }

      return field === field.toUpperCase()
        ? field[0].toUpperCase() + field.toLowerCase().slice(1)
        : field;
    },
  },
  computed: {
    computedPageSize() {
      return (
        this.pageSize ||
        this.$parent.$parent.$parent.$parent.page_size ||
        this.$parent.$parent.$parent.page_size ||
        this.$parent.$parent.page_size ||
        this.$parent.page_size
      );
    },
    minimumColumnSizes() {
      return (
        this.$parent.$parent.$parent.$parent.minimumColumnSizes ||
        this.$parent.$parent.$parent.minimumColumnSizes ||
        this.$parent.$parent.minimumColumnSizes ||
        this.$parent.minimumColumnSizes
      );
    },
    defaultColumnSizes() {
      return (
        this.$parent.$parent.$parent.$parent.defaultColumnSizes ||
        this.$parent.$parent.$parent.defaultColumnSizes ||
        this.$parent.$parent.defaultColumnSizes ||
        this.$parent.defaultColumnSizes
      );
    },
    isLoadingTableResult() {
      if (this.disableIsLoadingTalueResult) return false;

      return (
        this.$parent.$parent.$parent.$parent.isLoadingTableResult ||
        this.$parent.$parent.$parent.isLoadingTableResult ||
        this.$parent.$parent.isLoadingTableResult ||
        this.$parent.isLoadingTableResult
      );
    },

    isReady() {
      /*if (this.tier) {
        console.log('isReady: ', !this.loading, !this.isLoadingTableResult, this.data, this.defaultCellWidth)
        console.log("isReady!", !this.loading &&
          !this.isLoadingTableResult &&
          this.data &&
          this.defaultCellWidth)
      }*/
      return (
        !this.loading &&
        !this.isLoadingTableResult &&
        this.data &&
        (this.defaultCellWidth || this.ignoreDefaultWidth)
      );
    },
    isEmpty() {
      return this.data && !this.data.length;
    },
    computedColumnSizes() {
      const columnSizes = this.columnSizes || {};

      const sizes = {};
      const minimumColumnSizes = this.minimumColumnSizes;
      const defaultColumnSizes = this.defaultColumnSizes;

      Object.keys({
        ...(columnSizes || {}),
        ...(minimumColumnSizes || {}),
        ...(defaultColumnSizes || {}),
      }).forEach((fieldName) => {
        let desiredWidth = columnSizes[fieldName];

        const minimuWidth = minimumColumnSizes && minimumColumnSizes[fieldName];
        const defaultWidth =
          defaultColumnSizes && defaultColumnSizes[fieldName];

        if (!desiredWidth && defaultWidth) {
          desiredWidth = defaultWidth;
        }

        if (minimuWidth > desiredWidth || (!desiredWidth && minimuWidth)) {
          desiredWidth = minimuWidth;
        }

        // desiredWidth = minimuWidth

        sizes[fieldName] = desiredWidth;
      });

      const last = this.lastColumnVariable;
      if (last && sizes[last] < 175) sizes[last] = 220;

      return sizes;
    },
    lastColumnVariable() {
      return this.variables[this.variables.length - 1];
    },
    computedData() {
      const defaultCellWidth = this.defaultCellWidth;
      const sizes = this.computedColumnSizes;

      return this.data.map((row) => {
        this.variables.forEach((fieldName) => {
          row[fieldName] = this.parseField(row[fieldName]);

          row[`_style_${fieldName}`] =
            sizes && sizes[fieldName]
              ? {
                  "min-width": `${sizes[fieldName]}px !important`,
                  "max-width": `${sizes[fieldName]}px !important`,
                }
              : `width: ${defaultCellWidth}px`;
        });
        return row;
      });
    },
    dataOrPlaceholder() {
      const isReady = this.isReady;
      if (isReady) {
        const rows = this.computedData;
        rows.forEach(item => item._isReady = true)

        return rows
        // return rows.slice(0, 25)
      }

      const variables = this.variables;
      const sizes = this.computedColumnSizes;

      let placeholderData = Array.from(Array(this.computedPageSize).keys());
      placeholderData = placeholderData.map(() => {
        return variables.reduce(
          (obj, field) => ({
            ...obj,
            [`_style_${field}`]: sizes[field]
              ? {
                  "min-width": `${sizes[field]}px`,
                  "max-width": `${sizes[field]}px`,
                }
              : `flex-basis: 100%`,
          }),
          {}
        );
      });
      placeholderData.forEach((item) => (item._isReady = false));

      // return placeholderData.slice(0, 25)
      return placeholderData;
    },
    sizesStorageKey() {
      return SIZES_STORAGE_KEY + this.columnConfigurationId;
    },
  },
  watch: {
    columnSizes(sizes) {
      localStorage.setItem(this.sizesStorageKey, JSON.stringify(sizes));
    },
    variables(newVariables, oldVariables) {
      if (newVariables.length !== oldVariables.length) {
        this.columnSizes = null;
        this.calculateTheadCellWidths();
      }
      this.getStoredSizes()
    },
  },
};
</script>

<style>
.ComposableTable .FormCheckbox {
  padding: 4px;
}

.ComposableTable .btn-primary,
.ComposableTable .btn-secondary,
.ComposableTable .btn-danger,
.ComposableTable .btn-success {
  border: none !important;
  padding: 3px 6px !important;
}
</style>

<style scoped>
table {
  width: 100%;
}

.ComposableTable {
  max-height: 100vh;
  min-height: 500px;
  overflow: auto;
}

.noMinHeight.ComposableTable {
  min-height: auto;
}

.rowClickable {
  cursor: pointer;
}

.removeCursor {
  cursor: auto;
}

.rowClickable:hover td {
  background-color: rgb(245, 245, 245);
}

td {
  background-color: #FBFBFB;
}

.whiteBg td {
  background-color: white;
}

.empty-state-wrapper {
  flex-grow: 1;
  min-height: 400px;
}

tr + tr {
  border-top: 1px solid #efeff0;
}

thead tr {
  border-bottom: 1px solid #dee2e6;
}

th,
tr,
td,
table {
  flex-basis: 100%;
}

th,
td,
.cell-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 125px;
}

.last-table-cell {
  min-width: 175px;
}

td + td,
th + th {
  border-left: 0.5px solid #efeff0;
}

td {
  display: flex;
  align-items: center;
  padding: 0 12px !important;
  min-height: 30px;
}

thead th + th {
  border-left: 0.5px solid #dee2e6;
}

.cell-content {
  display: inline-block;
  max-width: 100%;
}

tr {
  position: relative;
}

.row-action {
  border: none;
  position: absolute;
  right: 0px;
  height: 100%;
  margin-right: 7px;
  max-width: 40px;
  padding: 0px !important;
  min-width: unset;
  overflow: visible;
  background-color: transparent !important;
  /* z-index: 10; */
}

.dropdown-menu {
  z-index: 10;
}

thead {
  position: sticky;
  inset-block-start: 0;
  z-index: 11;
}
</style>
